import { Select } from "antd";
import { useState } from "react";
import toast from "react-hot-toast";
import { useSelector } from "react-redux";
import { Col, Label, Modal, ModalBody, ModalHeader, Row } from "reactstrap";
import { mergeDnhCategories } from "../../api/dnhCategories.api";
import GeneralButton from "../button/GeneralButton";
import { Card, CardBody } from "reactstrap";

const SelectedCategoryInfo = ({ category }) => {
    if (!category) return null;

    const infoBoxes = [
        { label: "Category Name", value: category.categoryName },
        { label: "System ID", value: category.id },
        { label: "Identifiers", value: category.identifiers && category.identifiers.length > 0 ? category.identifiers.join(", ").toUpperCase() : "TECHMICRO" },
        { label: "Internal ID", value: category._id },
    ];

    return (
        <div className="mt-2">
            <Row className="g-2">
                {infoBoxes.map((box, index) => (
                    <Col md="4" sm="6" xs="12" key={index}>
                        <Card className="shadow-sm rounded-3">
                            <CardBody className="py-2 px-3 text-center">
                                <div className="fw-bold text-primary mb-1">{box.label}</div>
                                <div className="text-muted fs-12 text-truncate">{box.value || "—"}</div>
                            </CardBody>
                        </Card>
                    </Col>
                ))}
            </Row>
        </div>
    );
};

const MergingCategories = ({ modal, toggle }) => {
    const { all_categories } = useSelector((state) => state.redux);
    const [merging, setMerging] = useState(false);
    const [fromCategory, setFromCategory] = useState(null);
    const [toCategory, setToCategory] = useState(null);
    const [confirmMerge, setConfirmMerge] = useState(false);

    const categoryOptions = (all_categories || []).map((item) => ({
        value: item._id,
        label: `${item.categoryName} (${item.id}) - [${item.identifiers && item.identifiers.length > 0 ? item.identifiers.join(", ").toUpperCase() : "TECHMICRO"}]`,
    }));

    const handleSelectCategory = (value, type) => {
        const selected = (all_categories || []).find((i) => i?._id === value);

        if (!selected) {
            toast.error("Selected category not found.");
            return;
        }

        if (type === "from") {
            if (toCategory && toCategory._id === selected._id) {
                toast.error("You cannot select the same category for both fields.");
                return;
            }
            setFromCategory(selected);
        } else if (type === "to") {
            if (fromCategory && fromCategory._id === selected._id) {
                toast.error("You cannot select the same category for both fields.");
                return;
            }
            setToCategory(selected);
        }

        setConfirmMerge(false);
    };

    const handleMerge = async () => {
        if (!fromCategory || !toCategory) {
            toast.error("Please select both From and To categories.");
            return;
        }

        setMerging(true);
        const loadingToast = toast.loading("Merging categories...");

        await mergeDnhCategories(fromCategory._id, toCategory._id, (res) => {
            toast.dismiss(loadingToast);
            setMerging(false);

            if (res?.status === 200) {
                toast.success(res.data.message || "Categories merged successfully!");
                setFromCategory(null);
                setToCategory(null);
                setConfirmMerge(false);
                toggle();
            } else {
                toast.error(res?.message || "Something went wrong while merging categories.");
            }
        });
    };

    return (
        <Modal isOpen={modal} toggle={toggle} size="lg" centered>
            <ModalHeader toggle={toggle}>Merge DNH Categories</ModalHeader>
            <ModalBody>
                <Row>
                    <Col md={12}>
                        <Label className="fw-bold">From (Source Category - will be merged and removed)</Label>
                        <Select
                            size="large"
                            showSearch
                            placeholder="Search Category to merge FROM"
                            filterOption={(input, option) =>
                                option?.label?.toLowerCase().includes(input.toLowerCase())
                            }
                            onSelect={(value) => handleSelectCategory(value, "from")}
                            className="mb-2 w-100"
                            getPopupContainer={(trigger) => trigger.parentNode}
                            value={fromCategory ? fromCategory._id : undefined}
                            notFoundContent="No categories  found"
                            options={categoryOptions}
                        />
                        {fromCategory && <SelectedCategoryInfo category={fromCategory} />}
                    </Col>

                    <Col md={12} className="mt-4">
                        <Label className="fw-bold">To (Target Category - will receive merged data)</Label>
                        <Select
                            size="large"
                            showSearch
                            placeholder="Search Category to merge INTO"
                            filterOption={(input, option) =>
                                option?.label?.toLowerCase().includes(input.toLowerCase())
                            }
                            onSelect={(value) => handleSelectCategory(value, "to")}
                            className="mb-2 w-100"
                            getPopupContainer={(trigger) => trigger.parentNode}
                            value={toCategory ? toCategory._id : undefined}
                            notFoundContent="No categories found"
                            options={categoryOptions}
                        />
                        {toCategory && <SelectedCategoryInfo category={toCategory} />}
                    </Col>
                </Row>

                {fromCategory && toCategory && (
                    <div className="mt-4 p-3 border border-danger rounded bg-light" style={{ backgroundColor: "#fff6f6", borderColor: "#dc3545", color: "#b02a37" }}>
                        <strong>⚠️ Warning:</strong> Merging categories will update all products, subcategories, and relations associated with the source category to the target category.
                        <br />
                        This action <strong>cannot be undone</strong>.
                        <div className="form-check mt-3">
                            <input type="checkbox" id="confirmCategoryMerge" className="form-check-input" checked={confirmMerge} onChange={(e) => setConfirmMerge(e.target.checked)} />
                            <label htmlFor="confirmCategoryMerge" className="form-check-label">
                                I understand and confirm to proceed with the merge.
                            </label>
                        </div>
                    </div>
                )}

                <div className="d-flex justify-content-end mt-4">
                    <GeneralButton
                        title={"Merge Categories"}
                        className={"blue-button"}
                        width={"200px"}
                        handleClick={handleMerge}
                        disabled={merging || !confirmMerge}
                    />
                </div>
            </ModalBody>
        </Modal>
    );
};

export default MergingCategories;
